import React, { useEffect, useState } from "react";
import { Box, Typography, Grid } from "@material-ui/core";
import intl from "react-intl-universal";
import { Link } from "react-router-dom";
import { useMutate } from "restful-react";
import { useSnackbar } from "notistack";
import {
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  ReferenceLine,
  LineChart,
  Line,
  Legend,
  LegendValueFormatter,
} from "recharts";

import { routing } from "routing";
import { searchMatch, ISearchMatchInput, IMatchesResponse, IMatch } from "apis";
import { brand } from "MuiProvider/colors";

import { MatchTooltip } from "./MatchTooltip";

interface IAnnualMatchProps {
  teamId: number;
  startTime: string;
  endTime: string;
}

const ASPECT = 6;
const CHART_WIDTH = "90%";

const { Urls } = routing;

const calculateChartValue = (teamId: number, match: IMatch): number => {
  const selectTeamValue =
    match.homeFullTeam?.id === teamId ? match.homeScore : match.awayScore;
  const otherTeamValue =
    match.homeFullTeam?.id === teamId ? match.awayScore : match.homeScore;
  return selectTeamValue - otherTeamValue;
};

const buildChartDataFromMatches = (teamId: number, matches: IMatch[]) => {
  return matches.map((match, index) => {
    return {
      name: index.toString(),
      value: calculateChartValue(teamId, match),
      raw: match,
    };
  });
};

const buildMatchSummary = (teamId: number, matches: IMatch[]) => {
  const summary = {
    win: 0,
    lose: 0,
    draw: 0,
    totalScores: 0,
    totalLoseScores: 0,
    averageScores: 0,
    averageLoseScores: 0,
  };
  matches.map((match) => {
    if (match.homeFullTeam?.id === teamId) {
      if (match.homeScore > match.awayScore) {
        summary.win = summary.win + 1;
      } else if (match.homeScore < match.awayScore) {
        summary.lose = summary.lose + 1;
      } else {
        summary.draw = summary.draw + 1;
      }
      summary.totalScores = match.homeScore + summary.totalScores;
      summary.totalLoseScores = match.awayScore + summary.totalLoseScores;
    } else {
      if (match.homeScore > match.awayScore) {
        summary.lose = summary.lose + 1;
      } else if (match.homeScore < match.awayScore) {
        summary.win = summary.win + 1;
      } else {
        summary.draw = summary.draw + 1;
      }
      summary.totalScores = match.awayScore + summary.totalScores;
      summary.totalLoseScores = match.homeScore + summary.totalLoseScores;
    }
    return null;
  });
  if (matches.length !== 0) {
    summary.averageScores =
      Math.round((summary.totalScores / matches.length) * 10) / 10;
    summary.averageLoseScores =
      Math.round((summary.totalLoseScores / matches.length) * 10) / 10;
  }

  return summary;
};

export const AnnualMatch = ({
  teamId,
  startTime,
  endTime,
}: IAnnualMatchProps) => {
  const { mutate } = useMutate({
    verb: "POST",
    path: searchMatch(),
  });
  const { enqueueSnackbar } = useSnackbar();

  const [matches, setMatches] = useState<IMatch[]>([]);

  useEffect(() => {
    const input: ISearchMatchInput = {
      matchStartFrom: startTime,
      matchEndTo: endTime,
    };

    mutate(input).then((response: IMatchesResponse) => {
      if (response.errCode > 0) {
        enqueueSnackbar(response.errMessage, {
          variant: "error",
        });
        return;
      }
      setMatches(response.matches || []);
    });
  }, [enqueueSnackbar, mutate, startTime, endTime]);

  const chartData = buildChartDataFromMatches(teamId, matches);
  const summary = buildMatchSummary(teamId, matches);

  const legendFormatter: LegendValueFormatter = (value, entry) => {
    const label = intl.get("TEAM_DASHBOARD_LEGEND_GOAL_DIFFERENCE");
    return <span style={{ color: entry?.color }}>{label}</span>;
  };

  return (
    <>
      <Box mt={2} />
      <Link to={Urls.CALENDAR}>
        <Typography variant="h3">
          {intl.get("TEAM_DASHBOARD_ANNUAL_MATCH_1", {
            matchNum: matches.length,
          })}
        </Typography>
      </Link>
      <Box mt={2} />
      <Typography variant="h4">
        {intl.get("TEAM_DASHBOARD_ANNUAL_MATCH_2", {
          win: summary.win,
          lose: summary.lose,
          draw: summary.draw,
        })}
      </Typography>
      <Box mt={2} />
      <Typography variant="h4">
        {intl.get("TEAM_DASHBOARD_ANNUAL_MATCH_3", {
          totalScores: summary.totalScores,
          averageScores: summary.averageScores,
          totalLoseScores: summary.totalLoseScores,
          averageLoseScores: summary.averageLoseScores,
        })}
      </Typography>
      <Box mt={4} />
      <Grid container>
        <Grid item xs={12}>
          <ResponsiveContainer width={CHART_WIDTH} aspect={ASPECT}>
            <LineChart
              data={chartData}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip content={<MatchTooltip />} />
              <ReferenceLine y={0} stroke="#000" />
              <Line
                type="monotone"
                dataKey="value"
                stroke={brand.carrotOrange[100]}
                strokeWidth={4}
              />
              <Legend formatter={legendFormatter} />
            </LineChart>
          </ResponsiveContainer>
        </Grid>
      </Grid>
      <Box mt={4} />
    </>
  );
};
